import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, StatusBadge, LoadingSkeleton, EmptyState, ConfirmDialog } from "@/components/ui-system";
import { CourierSelect } from "./CourierSelect";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Truck, Ban, Copy, Check, User, Package, CreditCard, Receipt, Clock, AlertTriangle, MapPin, Loader2, Pencil,
} from "lucide-react";
import ChannelBadge from "@/components/b2c/ChannelBadge";
import ShippingLabelDownload from "@/components/b2c/ShippingLabelDownload";
import { RateMath } from "@/components/b2c/RateMath";
import { B2COrderEditDialog } from "@/components/b2c/B2COrderEditDialog";
import { B2COrderEditHistory } from "@/components/b2c/B2COrderEditHistory";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <dt className="text-sm text-slate-500 shrink-0">{label}</dt>
    <dd className={`text-sm text-slate-800 text-right break-words ${mono ? "font-mono" : ""}`}>{value ?? "-"}</dd>
  </div>
);

const B2COrderDetail = ({ user, onLogout }) => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [showCourierSelect, setShowCourierSelect] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/b2c/seller/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      setError(errDetail(e, "Could not load this order"));
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    axios.get(`${API}/wallet/balance`).then((r) => setWalletInfo(r.data)).catch(() => {});
  }, [fetchOrder]);

  const quoteInput = useMemo(() => {
    if (!order) return null;
    return {
      pickup_location_id: order.pickup_location_id || undefined,
      drop_pincode: order.buyer?.pincode,
      payment_mode: order.payment_mode,
      order_value: order.order_value,
      package: {
        dead_weight_kg: order.package?.dead_weight_kg,
        length_cm: order.package?.length_cm,
        breadth_cm: order.package?.breadth_cm,
        height_cm: order.package?.height_cm,
      },
    };
  }, [order]);

  const copyAwb = () => {
    if (!order?.awb) return;
    navigator.clipboard?.writeText(order.awb).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const doCancel = async () => {
    setCancelling(true);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/orders/${orderId}/cancel`);
      toast.success("Order cancelled — any wallet charge is auto-refunded.");
      setOrder(data);
      setCancelOpen(false);
    } catch (e) {
      toast.error(errDetail(e, "Could not cancel this order"));
    } finally {
      setCancelling(false);
    }
  };

  const canShip = can(user, "b2c.orders:ship");
  const canCancel = can(user, "b2c.orders:cancel");
  const isCancellable = order && ["NEW", "AWB_ASSIGNED", "PICKUP_SCHEDULED"].includes(order.status);
  const rate = order?.seller_rate;

  if (loading) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <LoadingSkeleton variant="detail" />
        </div>
      </SellerLayout>
    );
  }

  if (error || !order) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader title="B2C Order" backTo={() => navigate("/seller/b2c/orders")} />
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState
                icon={AlertTriangle}
                title="Order not found"
                description={error || "This order doesn't exist or you don't have access to it."}
                action={<Button variant="outline" onClick={() => navigate("/seller/b2c/orders")}>Back to orders</Button>}
              />
            </CardContent>
          </Card>
        </div>
      </SellerLayout>
    );
  }

  const history = Array.isArray(order.status_history) ? order.status_history : [];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title={order.order_number}
          subtitle={`Created ${formatToIST(order.created_at)}${order.reference_number ? ` · Ref: ${order.reference_number}` : ""}`}
          backTo={() => navigate("/seller/b2c/orders")}
          badge={
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={order.status} kind="b2c" size="lg" />
              {order.channel_name && (
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-1">
                  <ChannelBadge name={order.channel_name} logoUrl={order.channel_logo_url} />
                </span>
              )}
            </div>
          }
          actions={
            <>
              {order.status === "NEW" && (
                <Button variant="outline" onClick={() => setShowEdit(true)} data-testid="detail-edit-order">
                  <Pencil className="w-4 h-4 mr-2" /> Edit order
                </Button>
              )}
              {order.status === "NEW" && canShip && (
                <Button onClick={() => setShowCourierSelect((v) => !v)} data-testid="detail-ship-now">
                  <Truck className="w-4 h-4 mr-2" /> {showCourierSelect ? "Hide couriers" : "Ship Now"}
                </Button>
              )}
              {isCancellable && canCancel && (
                <Button variant="outline" className="text-red-600 hover:bg-red-50"
                  onClick={() => setCancelOpen(true)} data-testid="detail-cancel">
                  <Ban className="w-4 h-4 mr-2" /> Cancel
                </Button>
              )}
            </>
          }
        />

        {/* Auto-assign tried to ship at creation and failed (best-effort). */}
        {order.status === "NEW" && order.auto_ship_error && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-400">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Auto-assign didn't ship this order</p>
                  <p className="text-sm text-slate-600" data-testid="detail-autoship-error">{order.auto_ship_error}</p>
                  <p className="mt-1 text-xs text-slate-400">Fix the cause, then ship it with “Ship Now”. It stays New until then.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* AWB / courier strip */}
        {(order.awb || order.courier_code) && (
          <Card className="border-0 shadow-sm">
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between py-4">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {order.courier_code && (
                  <div>
                    <p className="text-xs text-slate-500">Courier</p>
                    <p className="text-sm font-semibold text-slate-800">{order.courier_name || order.courier_code}</p>
                  </div>
                )}
                {order.courier_mode && (
                  <div>
                    <p className="text-xs text-slate-500">Mode</p>
                    <p className="text-sm font-semibold capitalize text-slate-800">{order.courier_mode}</p>
                  </div>
                )}
                {order.awb ? (
                  <div>
                    <p className="text-xs text-slate-500">AWB</p>
                    <button onClick={copyAwb} className="inline-flex items-center gap-1.5 text-sm font-mono font-semibold text-slate-800 hover:text-brand"
                      data-testid="detail-awb-copy">
                      {order.awb}
                      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 opacity-60" />}
                    </button>
                  </div>
                ) : (order.status && !["NEW", "CANCELLED"].includes(order.status)) ? (
                  <div>
                    <p className="text-xs text-slate-500">AWB</p>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-600" data-testid="detail-awb-pending"
                      title="The courier assigns the AWB after manifest — it will appear here automatically.">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Assignment in progress
                    </span>
                  </div>
                ) : null}
              </div>
              {order.awb && (
                <ShippingLabelDownload apiBase="b2c/seller" orderId={order.id} className="sm:min-w-[210px]" />
              )}
            </CardContent>
          </Card>
        )}

        {/* Courier select (NEW order, Ship Now) */}
        {order.status === "NEW" && showCourierSelect && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="w-5 h-5 text-brand" /> Choose a courier
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CourierSelect
                quoteInput={quoteInput}
                orderId={order.id}
                walletBalance={walletInfo?.balance}
                onShipped={(updated) => {
                  setShowCourierSelect(false);
                  if (updated?.id) setOrder(updated);
                  else fetchOrder();
                }}
              />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: timeline */}
          <Card className="border-0 shadow-sm lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-brand" /> Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500">No status updates yet.</p>
              ) : (
                <ol className="relative border-l border-slate-200 ml-2 space-y-5">
                  {history.map((h, i) => (
                    <li key={i} className="ml-4" data-testid={`timeline-${i}`}>
                      <span className={`absolute -left-1.5 mt-1 h-3 w-3 rounded-full ${i === history.length - 1 ? "bg-brand" : "bg-slate-300"}`} />
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge status={h.status} kind="b2c" size="sm" />
                        {h.note && <span className="text-xs text-slate-500">{h.note}</span>}
                      </div>
                      {(h.by || h.role) && (
                        <p className="mt-0.5 text-xs text-slate-500">
                          by {h.by || "—"}{h.role ? ` · ${h.role}` : ""}
                        </p>
                      )}
                      {h.at && <p className="mt-0.5 text-xs text-slate-400">{formatToIST(h.at)}</p>}
                    </li>
                  ))}
                </ol>
              )}

              {/* Full courier tracking (every scan from the aggregator), newest first */}
              {Array.isArray(order.tracking?.checkpoints) && order.tracking.checkpoints.length > 0 && (
                <div className="mt-5 border-t border-slate-100 pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Courier tracking{order.tracking?.status_raw ? ` · ${order.tracking.status_raw}` : ""}
                  </p>
                  <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                    {[...order.tracking.checkpoints]
                      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
                      .map((c, i) => (
                        <li key={i} className="ml-4">
                          <span className={`absolute -left-1.5 mt-1 h-2.5 w-2.5 rounded-full ${i === 0 ? "bg-brand" : "bg-slate-300"}`} />
                          <p className="text-sm text-slate-800">{c.status || c.remark || c.scan || "—"}</p>
                          {(c.location || c.scan) && (
                            <p className="text-xs text-slate-500">{c.location || ""}{c.scan ? `${c.location ? " · " : ""}${c.scan}` : ""}</p>
                          )}
                          {c.timestamp && <p className="text-xs text-slate-400">{formatToIST(c.timestamp)}</p>}
                        </li>
                      ))}
                  </ol>
                </div>
              )}

              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Edit history</p>
                <B2COrderEditHistory editHistory={order.edit_history} />
              </div>
            </CardContent>
          </Card>

          {/* Right: buyer / package / payment / charges */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="w-5 h-5 text-brand" /> Buyer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl>
                  <Row label="Name" value={order.buyer?.name} />
                  <Row label="Phone" value={order.buyer?.phone} />
                  {order.buyer?.alt_phone && <Row label="Alt phone" value={order.buyer.alt_phone} />}
                  {order.buyer?.email && <Row label="Email" value={order.buyer.email} />}
                  <Row
                    label="Address"
                    value={
                      <span className="inline-flex items-start gap-1 justify-end">
                        <MapPin className="w-3.5 h-3.5 mt-0.5 text-slate-400 shrink-0" />
                        <span>
                          {order.buyer?.address}
                          {order.buyer?.landmark ? `, ${order.buyer.landmark}` : ""}
                          {", "}
                          {[order.buyer?.city, order.buyer?.state, order.buyer?.pincode].filter(Boolean).join(", ")}
                        </span>
                      </span>
                    }
                  />
                </dl>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-brand" /> Package
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Dead weight" value={`${order.package?.dead_weight_kg ?? "-"} kg`} />
                    <Row label="Volumetric" value={`${order.package?.volumetric_kg ?? "-"} kg`} />
                    <Row label="Charged weight" value={<span className="font-semibold">{order.package?.charged_weight_kg ?? "-"} kg</span>} />
                    <Row label="Dimensions" value={`${order.package?.length_cm ?? "-"}×${order.package?.breadth_cm ?? "-"}×${order.package?.height_cm ?? "-"} cm`} />
                    {order.zone && <Row label="Zone" value={<Badge variant="outline" className="border-slate-200">{order.zone}</Badge>} />}
                  </dl>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-brand" /> Payment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <dl>
                    <Row label="Mode" value={order.payment_mode === "cod" ? "Cash on Delivery" : "Prepaid"} />
                    <Row label="Order value" value={inr(order.order_value)} />
                    {order.payment_mode === "cod" && <Row label="COD to collect" value={<span className="font-semibold text-amber-700">{inr(order.cod_amount)}</span>} />}
                    <Row label="Items" value={`${order.products?.length ?? 0}`} />
                  </dl>
                </CardContent>
              </Card>
            </div>

            {/* Products */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-brand" /> Items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {(order.products || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                      <p className="text-xs text-slate-500">
                        {p.sku ? `SKU ${p.sku} · ` : ""}Qty {p.qty} × {inr(p.unit_price)}
                        {p.discount ? ` · −${inr(p.discount)}` : ""}
                        {p.tax_pct ? ` · ${p.tax_pct}% tax` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Shipping charge breakdown (white-label — seller rate only, never our cost) */}
            {rate && (
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Shipping charges</CardTitle>
                </CardHeader>
                <CardContent>
                  <RateMath rate={rate} totalLabel="Wallet debit" />
                  {order.wallet_transaction_id && (
                    <p className="mt-2 text-xs text-slate-400">
                      Charged from wallet ·{" "}
                      <button className="text-brand hover:underline" onClick={() => navigate("/seller/wallet")}>
                        View wallet
                      </button>
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={`Cancel order ${order.order_number}?`}
        description="This cancels the shipment before pickup. If the order was already shipped, the wallet charge is automatically refunded."
        confirmLabel="Cancel order"
        tone="destructive"
        loading={cancelling}
        onConfirm={doCancel}
      />

      <B2COrderEditDialog
        order={order}
        open={showEdit}
        onOpenChange={setShowEdit}
        saveUrl={`${API}/b2c/seller/orders/${orderId}`}
        pickupUrl={`${API}/pickup-locations/`}
        onSaved={(updated) => { if (updated) setOrder(updated); fetchOrder(); }}
      />
    </SellerLayout>
  );
};

export default B2COrderDetail;
